import React, { useState, useRef, useEffect, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { ColumnTypes } from '../../../models/SheetModel';
import { ColumnRowValidationEditor } from '../Column/ColumnRowValidationEditor';
import Combobox from '../Combobox/Combobox';
import SelectDropdown from '../Dropdown/SelectDropdown';
import InputText from '../Input/InputText';
import Textarea from '../Textarea/Textarea';
import InputNumber from '../Input/InputNumber';
import InputDate from '../Input/InputDate';
import { InputCheckbox } from '../Input/InputCheckbox';
import InputUrl from '../Input/InputUrl';
import InputEmail from '../Input/InputEmail';
import { InputRating } from '../Input/InputRating';
import InputPercentage from '../Input/InputPercentage';
import Badge from '../Badge/Badge';
import {
    ArrowLeftIcon,
    ArrowRightIcon,
    ArrowUturnLeftIcon,
    ArrowUturnRightIcon,
    PlusIcon,
    TrashIcon,
    EllipsisVerticalIcon,
    CheckIcon,
    Bars2Icon,
    DocumentTextIcon,
    AtSymbolIcon,
    LinkIcon,
    CalendarIcon,
    RocketLaunchIcon,
    FlagIcon,
    ListBulletIcon,
    TagIcon,
    UserIcon,
    StarIcon,
    CheckCircleIcon,
    HashtagIcon,
    CurrencyDollarIcon
} from '@heroicons/react/20/solid'; // Use solid for actions
import {
    Palette,
    Check
} from "@phosphor-icons/react";

// Map emoji icons to Heroicons (copied from TableHeader, consider centralizing this)
const iconMap = {
    '📝': <Bars2Icon className="h-4 w-4" />,
    '📄': <DocumentTextIcon className="h-4 w-4" />,
    '✉️': <AtSymbolIcon className="h-4 w-4" />,
    '🔗': <LinkIcon className="h-4 w-4" />,
    '📅': <CalendarIcon className="h-4 w-4" />,
    '🏁': <RocketLaunchIcon className="h-4 w-4" />,
    '🏆': <FlagIcon className="h-4 w-4" />,
    '📋': <ListBulletIcon className="h-4 w-4" />,
    '🔄': <TagIcon className="h-4 w-4" />,
    '👤': <UserIcon className="h-4 w-4" />,
    '⭐': <StarIcon className="h-4 w-4" />,
    '✅': <CheckCircleIcon className="h-4 w-4" />,
    '%': <CurrencyDollarIcon className="h-4 w-4" />,
    '#': <HashtagIcon className="h-4 w-4" />
};
const getIcon = (emojiIcon) => iconMap[emojiIcon] || <span>{emojiIcon}</span>;

// Helper function to place caret at end (Works for contentEditable and input/textarea)
const placeCaretAtEnd = (element) => {
    if (!element || typeof window === 'undefined') return;

    // For contentEditable elements
    if (element.isContentEditable) {
        element.focus(); // Ensure focus first
        const range = document.createRange();
        const selection = window.getSelection();
        if (!selection) return;
        range.selectNodeContents(element);
        range.collapse(false); // false means collapse to end
        selection.removeAllRanges();
        selection.addRange(range);
    }
    // For input/textarea elements
    else if (typeof element.selectionStart === 'number') {
        element.focus(); // Ensure focus first
        // Use setTimeout to ensure focus is set before trying to set selection
        setTimeout(() => {
            try {
                const length = element.value.length;
                element.selectionStart = element.selectionEnd = length;
            } catch (e) {
                // Some input types (like 'date') might throw errors here
                console.warn("Could not set caret position for input type:", element.type, e);
            }
        }, 0);
    } else {
        // Fallback for other types if necessary
        element.focus();
    }
};

export const SidePanelEditorRow = observer(({
    column,
    value,
    viewModel,
    isFirst,
    isLast,
    rowIndex,
    isSelected,
    onSelect
}) => {
    const [isEditingHeading, setIsEditingHeading] = useState(false);
    const [typeComboboxOpen, setTypeComboboxOpen] = useState(false);
    const [actionsDropdownOpen, setActionsDropdownOpen] = useState(false);
    const [isValueComboboxOpen, setIsValueComboboxOpen] = useState(false);
    const headingRef = useRef(null);
    const comboboxInputRef = useRef(null); // For type combobox
    const containerRef = useRef(null); // Ref for the row container
    const inputRef = useRef(null); // Add ref for the input element
    
    // Add a key to force re-render when the selected item changes
    // This is crucial - we need to use the column ID and a unique identifier for the row
    const rowKey = `${column?.id}-${rowIndex}-${viewModel.selectedRowId}`;
    
    // Force reset of input state when selectedRowId changes
    useEffect(() => {
        // This effect will run whenever the viewModel.selectedRowId changes
        // It ensures we reset the component state completely
        // For date inputs, we need to ensure they're properly reset
        if (inputRef.current && (column.type?.id === 'DATE' || 
                                column.type?.id === 'START_DATE' || 
                                column.type?.id === 'END_DATE')) {
            // Force the date input to reset
            if (inputRef.current.resetValue) {
                inputRef.current.resetValue(value);
            }
        }
        
        console.log(`SidePanelEditorRow reset for ${column?.heading} due to row change, new value:`, value);
    }, [viewModel.selectedRowId, value, column?.heading, column.type?.id]);
    
    // --- Heading Editing Logic (from TableHeader) ---
    useEffect(() => {
        if (isEditingHeading && headingRef.current) {
            headingRef.current.textContent = column.heading;
            placeCaretAtEnd(headingRef.current);
        }
    }, [isEditingHeading, column.heading]);

    const handleHeadingFocus = () => setIsEditingHeading(true);

    const handleHeadingBlur = () => {
        setIsEditingHeading(false);
        if (headingRef.current) {
            const newHeading = headingRef.current.textContent?.trim() || 'Untitled';
            if (newHeading !== column.heading) {
                viewModel.updateColumnHeading(column.id, newHeading);
            }
        }
    };

    const handleHeadingKeyDown = (e) => {
        if (!isEditingHeading) return;
        e.stopPropagation();
        if (e.key === 'Enter') {
            e.preventDefault();
            headingRef.current?.blur();
        } else if (e.key === 'Escape') {
            e.preventDefault();
            if (headingRef.current) {
                headingRef.current.textContent = column.heading; // Revert
            }
            headingRef.current?.blur();
        }
    };

    // --- Type Selection Logic (from TableHeader) ---
    const typeOptions = Object.values(ColumnTypes).map(type => ({
        value: type.id,
        label: type.label,
        icon: type.icon, // Keep original icon if needed elsewhere
        emojiIcon: type.icon // Use emojiIcon for consistency with getIcon
    }));

    const renderTypeOption = (option, { isSelected }) => (
        <div className="flex items-center gap-2 py-0.5">
            <span className="text-gray-400">{getIcon(option.emojiIcon)}</span>
            <span className="text-xs">{option.label}</span>
            {isSelected && <CheckIcon className="h-4 w-4 ml-auto text-purple-500" />}
        </div>
    );

    const handleTypeChange = (selectedOption) => {
        if (selectedOption?.value) {
            const newType = ColumnTypes[selectedOption.value];
            if (newType && newType !== column.type) {
                viewModel.updateColumnType(column.id, newType);
            }
            setTypeComboboxOpen(false);
        }
    };

    const handleIconClick = (e) => {
        e.stopPropagation();
        setTypeComboboxOpen(true);
        setTimeout(() => comboboxInputRef.current?.focus(), 50);
    };

    // --- Column Actions Logic (from TableHeader) ---
    const getColumnActionOptions = () => [
        { label: "Move up", icon: <ArrowLeftIcon className="h-4 w-4" />, value: "moveLeft", disabled: isFirst },
        { label: "Move down", icon: <ArrowRightIcon className="h-4 w-4" />, value: "moveRight", disabled: isLast },
        { type: "divider" },
        { label: "Insert above", icon: <ArrowUturnLeftIcon className="h-4 w-4" />, value: "addLeft" },
        { label: "Insert below", icon: <ArrowUturnRightIcon className="h-4 w-4" />, value: "addRight" },
        { type: "divider" },
        { label: "Delete property", icon: <TrashIcon className="h-4 w-4" />, value: "delete", destructive: true },
    ];

    const handleColumnActionSelect = (action) => {
        switch (action) {
            case "moveLeft": viewModel.moveColumnLeft(column.id); break;
            case "moveRight": viewModel.moveColumnRight(column.id); break;
            case "addLeft": viewModel.addColumnLeft(column.id); break;
            case "addRight": viewModel.addColumnRight(column.id); break;
            case "delete": viewModel.deleteColumn(column.id); break;
            default: break;
        }
        setActionsDropdownOpen(false); // Close dropdown after action
    };

    // --- Value Input Rendering Logic (from TableCell) ---
    const handleInputChange = useCallback((newValue) => {
        let valueToStore = newValue;
        const typeId = column.type?.id;

        // If the input component sends a raw string for URL/Email, format it here
        // This ensures the correct object format is stored even if the component calls onChange per keystroke with raw text.
        if ((typeId === 'URL' || typeId === 'EMAIL') && typeof newValue === 'string') {
            console.log(`SidePanelEditorRow: Formatting raw string input for ${typeId}:`, newValue);
            if (typeId === 'EMAIL') {
                // Ensure empty strings result in null or an empty object representation if desired
                valueToStore = newValue.trim() ? { text: newValue, hyperlink: `mailto:${newValue}` } : null; // Or { text: '', hyperlink: '' }
            } else { // URL
                valueToStore = newValue.trim() ? { text: newValue, hyperlink: newValue } : null; // Or { text: '', hyperlink: '' }
            }
        }
        // Ensure Checkbox always stores boolean
        else if (typeId === 'CHECKBOX') {
            // The InputCheckbox onChange likely passes the boolean state directly
            valueToStore = !!newValue;
        }
        // Ensure Rating stores number or null
        else if (typeId === 'RATING') {
            // The InputRating onChange likely passes the number directly
            const num = parseInt(newValue, 10);
            valueToStore = !isNaN(num) && num >= 0 ? num : null; // Store valid number or null
        }
        // Add other type coercions if necessary...


        // Update the viewModel directly
        console.log(`SidePanelEditorRow: Updating field ${column.id} with value:`, valueToStore);
        viewModel.updateField(column.id, valueToStore);
    }, [viewModel, column.id, column.type?.id]); // Add column.type.id dependency

    // Helper function to handle validation options updates
    const handleUpdateValidationOptions = useCallback((option, action) => {
        if (!column || !column.validationOptions) return;
        
        let newOptions = [...column.validationOptions];
        
        if (action === 'create' && option && !newOptions.includes(option)) {
            // Add the new option
            newOptions.push(option);
        } else if (action === 'remove' && option) {
            // Remove the option
            newOptions = newOptions.filter(opt => opt !== option);
        }
        
        // Call the viewModel to update validation options
        viewModel.updateColumnValidation(column.id, newOptions);
    }, [column, viewModel]);

    // Update Combobox change handler to also update our local open state
    const handleValueComboboxChange = useCallback((selectedOption) => {
        // For single select, selectedOption is the chosen object or null
        const newValue = selectedOption ? selectedOption.value : null;
        console.log(`SidePanelEditorRow: Combobox onChange for column ${column.id}, new value:`, newValue);
        viewModel.updateField(column.id, newValue);
        setIsValueComboboxOpen(false); // Close combobox on selection
    }, [viewModel, column.id]);

    // Handler for creating a new option (for Select/Status and Person)
    const handleCreateOption = (newOption) => {
        if (newOption && newOption.label) {
            const newValue = newOption.label; // Use the label as the new value
            console.log(`SidePanelEditorRow: Creating option for column ${column.id}, new value:`, newValue);
            // 1. Update the current cell's value
            viewModel.updateField(column.id, newValue);
            // 2. Add the new option to the column's validation list
            viewModel.updateValidationOptionsForColumn(column.id, newValue, 'create');
            // 3. Close the combobox
            setIsValueComboboxOpen(false);
        }
    };

    // Handler for removing an option (optional, but good practice)
    const handleRemoveOption = (removedOption) => {
        if (removedOption && removedOption.value) {
             console.log(`SidePanelEditorRow: Removing option for column ${column.id}, value:`, removedOption.value);
            // Update the column's validation list
            viewModel.updateValidationOptionsForColumn(column.id, removedOption.value, 'remove');
            // Note: We might not need to update the field value here unless
            // the removed option was the currently selected one.
            // The Combobox component itself handles removing it from the selection.
        }
    };

    const renderValueInput = useCallback(() => {
        const typeId = column.type?.id || 'SINGLE_LINE_TEXT';
        const inputClassName = "w-full text-xs";
        
        const fieldValue = viewModel.getFieldValue(column.id);
        
        const commonProps = {
            ref: inputRef,
            // Pass the raw fieldValue; Input components should handle internal display formatting
            // Or pass the formatted value if the component strictly requires it (like InputUrl/InputEmail might)
            // value: fieldValue, // Let's try passing raw value first
            onChange: handleInputChange, // handleInputChange now handles formatting before storing
            className: `${inputClassName} ${isFirst ? 'focus-target' : ''}`,
            tabIndex: 0,
            // Force component recreation when row changes
            key: `${column.id}-${viewModel.selectedRowId}-${typeId}`
        };

        switch (typeId) {
            case 'SINGLE_LINE_TEXT':
                return <InputText {...commonProps} value={fieldValue ?? ''} placeholder="Empty" />;
            case 'MULTI_LINE_TEXT':
                // Use Textarea for multi-line with more rows
                return <Textarea {...commonProps} value={fieldValue ?? ''} placeholder="Empty" rows={8} />; // Increased rows to 8 (adjust as needed)
            case 'NUMBER':
                return <InputNumber {...commonProps} value={fieldValue} placeholder="Empty" />; // InputNumber likely handles null/undefined
            case 'DATE':
            case 'START_DATE':
            case 'END_DATE':
                // InputDate expects the raw value (Date object or null)
                return <InputDate {...commonProps} value={fieldValue} />; // Pass raw value
            case 'URL':
                // InputUrl expects an object { text: string, hyperlink: string } or null/undefined
                // Prepare the value prop based on fieldValue, but handleInputChange ensures correct storage format
                const urlValue = typeof fieldValue === 'object' && fieldValue !== null && 'text' in fieldValue && 'hyperlink' in fieldValue
                    ? fieldValue
                    : (typeof fieldValue === 'string' && fieldValue.trim() ? { text: fieldValue, hyperlink: fieldValue } : { text: '', hyperlink: '' }); // Default empty object
                return <InputUrl {...commonProps} value={urlValue} onChange={handleInputChange} placeholder="Empty" />;
            case 'EMAIL':
                 // InputEmail expects an object { text: string, hyperlink: string } or null/undefined
                 // Prepare the value prop based on fieldValue, but handleInputChange ensures correct storage format
                 const emailValue = typeof fieldValue === 'object' && fieldValue !== null && 'text' in fieldValue && 'hyperlink' in fieldValue
                    ? fieldValue
                    : (typeof fieldValue === 'string' && fieldValue.trim() ? { text: fieldValue, hyperlink: `mailto:${fieldValue}` } : { text: '', hyperlink: '' }); // Default empty object
                return <InputEmail {...commonProps} value={emailValue} onChange={handleInputChange} placeholder="Empty" />;
            case 'CHECKBOX':
                 const handleCheckboxChange = (isChecked) => {
                    // Pass the boolean directly to handleInputChange
                    handleInputChange(isChecked);
                 };
                 // InputCheckbox's value prop is 'checked'
                return <InputCheckbox {...commonProps} checked={!!fieldValue} onChange={handleCheckboxChange} className="ml-1" tabIndex={0} />;
            case 'RATING':
                 // InputRating's value prop expects a number
                 // Pass the numeric value directly, handleInputChange ensures correct storage format
                return <InputRating {...commonProps} value={fieldValue ?? 0} onChange={handleInputChange} className="" tabIndex={0} />; // Remove w-full if it breaks layout
            case 'PERCENTAGE':
                // InputPercentage should handle the '%' display. Pass the raw number.
                return <InputPercentage {...commonProps} value={fieldValue} placeholder="Empty" />; // InputPercentage likely handles null/undefined

            case 'SELECT':
            case 'STATUS':
                const selectOptions = column.validationOptions?.map(option => {
                    const styleRule = column.optionStyles?.get(option);
                    return { 
                        value: option, 
                        label: option,
                        bgColor: styleRule?.fill?.fgColor?.argb ? 
                            `#${styleRule.fill.fgColor.argb.slice(-6)}` : 'default',
                        textColor: styleRule?.font?.color?.argb ? 
                            `#${styleRule.font.color.argb.slice(-6)}` : 'default'
                    };
                }) || [];
                
                // Determine badge colors for current value
                let badgeBgColor = 'default';
                let badgeTextColor = 'default';

                if (fieldValue && column.optionStyles && column.optionStyles.size > 0) {
                    const styleRule = column.optionStyles.get(String(fieldValue));
                    if (styleRule) {
                        // Extract colors from ExcelJS style object
                        badgeBgColor = styleRule.fill?.fgColor?.argb ? `#${styleRule.fill.fgColor.argb.slice(-6)}` : 'default';
                        badgeTextColor = styleRule.font?.color?.argb ? `#${styleRule.font.color.argb.slice(-6)}` : 'default';
                    }
                }
                
                // Ensure the current value is formatted with the correct colors
                const selectValueArray = fieldValue ? [{
                    value: fieldValue, 
                    label: fieldValue,
                    bgColor: badgeBgColor,
                    textColor: badgeTextColor
                }] : [];
                
                // Then add the style management option
                if (column.type?.hasCustomValidation) {
                    selectOptions.push({ 
                        value: '__manage_styles__', 
                        label: 'Manage Option Styles',
                        isAction: true
                    });
                }
                
                return (
                    <div className="side-panel-editor-row__input-container w-full">
                        <Combobox
                            options={selectOptions}
                            value={selectValueArray}
                            onChange={(selectedOption) => {
                                if (selectedOption && selectedOption.isAction) {
                                    if (selectedOption.value === '__manage_styles__') {
                                        // Call the viewModel's method to open style editor
                                        viewModel.openColumnStyleEditor(column.id);
                                    }
                                } else {
                                    handleValueComboboxChange(selectedOption);
                                }
                            }}
                            renderOption={(option, { isSelected }) => (
                                option.isAction ? (
                                    <div className="flex items-center gap-2 text-gray-500">
                                        <Palette className="h-4 w-4" />
                                        <span>{option.label}</span>
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-2">
                                        {option.value && (
                                            <Badge
                                                text={option.label}
                                                bgColor={option.bgColor}
                                                textColor={option.textColor}
                                            />
                                        )}
                                        {isSelected && <Check className="h-4 w-4 ml-auto text-lime-gree-800" />}
                                    </div>
                                )
                            )}
                            onCreateOption={handleCreateOption}
                            onRemoveOption={handleRemoveOption}
                            placeholder={`Select ${typeId === 'STATUS' ? 'status' : 'option'}...`}
                            className={`${inputClassName} w-full`}
                            tabIndex={0}
                            key={`${column.id}-${viewModel.selectedRowId}-${typeId}-combobox`}
                            // Control open state
                            isOpen={isValueComboboxOpen}
                            onOpenChange={setIsValueComboboxOpen}
                            displayType="badge"
                            withBorder={false}
                            showFocusRing={false}
                            showDropdownButton={true}
                            allowCreation={true}
                            allowRemoval={true}
                            multiSelect={false}
                        />
                    </div>
                );

            case 'PERSON':
                const personOptions = column.validationOptions?.map(option => ({ 
                    value: option, 
                    label: option 
                })) || [];
                
                const personValueArray = fieldValue ? [{ value: fieldValue, label: fieldValue }] : [];
                
                return (
                    <div className="side-panel-editor-row__input-container w-full">
                        <Combobox
                            options={personOptions}
                            value={personValueArray}
                            onChange={handleValueComboboxChange}
                            onCreateOption={handleCreateOption}
                            onRemoveOption={handleRemoveOption}
                            placeholder="Select person..."
                            className={`${inputClassName} w-full`}
                            tabIndex={0}
                            key={`${column.id}-${viewModel.selectedRowId}-${typeId}-combobox`}
                            // Control open state
                            isOpen={isValueComboboxOpen}
                            onOpenChange={setIsValueComboboxOpen}
                            displayType="avatar"
                            withBorder={false}
                            showFocusRing={false}
                            showDropdownButton={true}
                            allowCreation={true}
                            allowRemoval={true}
                            multiSelect={false}
                        />
                    </div>
                );

            default:
                // Display raw value for unhandled types, ensuring it's a string
                const displayValue = fieldValue !== null && fieldValue !== undefined ? String(fieldValue) : 'Empty';
                return <span className="text-gray-500 text-xs italic">{displayValue}</span>;
        }
    }, [column, viewModel, handleInputChange, handleValueComboboxChange, handleUpdateValidationOptions, isValueComboboxOpen]);

    // Keep the debugging logs if they're helpful
    useEffect(() => {
        console.log(`Row ${rowIndex} (${column.heading}) mounted, isFirst=${isFirst}`);
        
        return () => {
            console.log(`Row ${rowIndex} (${column.heading}) unmounting`);
        };
    }, [rowIndex, column.heading, isFirst]);

    // Effect to focus input or container when the row becomes selected
    useEffect(() => {
        // Add null check for containerRef.current
        if (isSelected && containerRef.current) {
            requestAnimationFrame(() => {
                // Ensure containerRef.current still exists inside requestAnimationFrame
                if (!containerRef.current) return;
                
                const focusableInput = containerRef.current.querySelector(
                    '.value-input-container input:not([type=checkbox]), .value-input-container textarea, .value-input-container [contenteditable="true"]'
                );

                if (focusableInput) {
                    console.log(`Row ${rowIndex} (${column.heading}): Focusing input and placing caret at end`, focusableInput);
                    placeCaretAtEnd(focusableInput);
                } else {
                    // Fallback: Focus the row container itself if no specific input found
                    console.log(`Row ${rowIndex} (${column.heading}): No specific input found, focusing container.`);
                    containerRef.current.focus({ preventScroll: true }); // Focus the row div
                }
            });
        }
    }, [isSelected, rowIndex, column?.heading]); // Rerun when selection state changes

    // Effect to close the value combobox when the row is deselected
    useEffect(() => {
        if (!isSelected && isValueComboboxOpen) {
            console.log(`Row ${rowIndex} (${column.heading}): Deselected, closing value combobox.`);
            setIsValueComboboxOpen(false);
        }
        // We don't necessarily want to open it automatically when selected,
        // only close it when deselected.
    }, [isSelected, isValueComboboxOpen, rowIndex, column?.heading]);

    return (
        <div
            ref={containerRef}
            className={`flex items-start w-full p-2 bg-white group text-xs cursor-pointer relative ${isSelected ? 'outline-none border-t border-b border-indigo-600' : 'border-t border-t-transparent border-b'}`} // Using box-shadow for selected state to avoid layout shift
            onClick={onSelect}
            // Make the row container programmatically focusable, but not via sequential Tab navigation
            tabIndex={-1}
            key={`row-container-${column.id}-${viewModel.selectedRowId}`}
            data-row-index={rowIndex}
        >
            {/* Column Header Part */}
            <div className="w-1/3 pr-1 flex items-center sticky top-0" onClick={(e) => e.stopPropagation()} /* Add position: relative */ >
                 {/* Type Icon (Clickable) */}
                 <span
                    className="flex-shrink-0 w-6 h-6 mr-1.5 flex items-center justify-center rounded border border-transparent hover:border-gray-200 hover:bg-gray-200 text-gray-400 hover:text-gray-500 cursor-pointer transition-all duration-150"
                    title={`${column.type.label} - Click to change type`}
                    onClick={handleIconClick}
                >
                    {getIcon(column.type.icon)}
                </span>

                {/* Editable Heading */}
                <div
                    ref={headingRef}
                    contentEditable={true}
                    suppressContentEditableWarning={true}
                    onFocus={handleHeadingFocus}
                    onBlur={handleHeadingBlur}
                    onKeyDown={handleHeadingKeyDown}
                    tabIndex="-1"
                    className={`flex-grow text-gray-500 text-xs outline-none ${isEditingHeading ? 'cursor-text' : 'cursor-text'}`}
                    style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                >
                    {column.heading}
                </div>

                 {/* Actions Dropdown */}
                 <div className="ml-auto flex-shrink-0">
                     <SelectDropdown
                        placeholder=""
                        options={getColumnActionOptions()}
                        onChange={handleColumnActionSelect}
                        stopPropagation={true}
                        showDropdownButton={false}
                        onOpenChange={setActionsDropdownOpen}
                        isOpen={actionsDropdownOpen}
                        customTrigger={
                            <div className={`h-6 w-6 flex items-center justify-center rounded border ${actionsDropdownOpen ? 'border-gray-200 bg-gray-100' : 'border-transparent hover:border-gray-200 hover:bg-white'} cursor-pointer transition-all duration-150 ring-0 focus:ring-0`}>
                                <EllipsisVerticalIcon
                                    className={`h-3.5 w-3.5 ${actionsDropdownOpen ? 'text-gray-500' : isSelected ? 'text-gray-400' : 'text-transparent group-hover:text-gray-400'} hover:text-gray-500`}
                                />
                            </div>
                        }
                    />
                 </div>

                 {/* Type Combobox (Hidden, absolutely positioned container - no offset) */}
                 <div className="absolute"> {/* Absolute container, removed top-full and left-0 */}
                     <div style={{ opacity: 0, pointerEvents: typeComboboxOpen ? 'auto' : 'none' }}> {/* Invisible content */}
                         {typeComboboxOpen && ( // Conditionally render Combobox only when needed
                             <Combobox
                                 inputRef={comboboxInputRef}
                                 options={typeOptions}
                                 // Pass the current type to ensure it's selected in the dropdown list
                                 value={[{
                                     value: column.type.id,
                                     label: column.type.label,
                                     icon: column.type.icon, // Pass icon data if needed by renderOption
                                     emojiIcon: column.type.icon // Pass emojiIcon data
                                 }]}
                                 onChange={handleTypeChange}
                                 onOpenChange={setTypeComboboxOpen}
                                 isOpen={typeComboboxOpen}
                                 renderOption={renderTypeOption}
                                 renderSelectedItem={() => null} // Don't render selection in the hidden input
                                 placeholder="Change property type..."
                                 showDropdownButton={false}
                                 withBorder={false} // Match TableHeader
                                 className="text-xs" // Apply text-xs to dropdown content
                                 tabIndex={-1} // Make it non-tabbable as it's hidden
                                 containerRef={document.body} // Portal dropdown
                                 stopPropagation={true} // Prevent clicks inside dropdown from bubbling
                                 position="bottom" // Position dropdown below trigger
                                 maxHeight={250} // Limit dropdown height
                                 customStyles={{ // Match TableHeader styles
                                     option: "py-1.5 px-3 text-xs",
                                     dropdown: "text-xs shadow-lg"
                                 }}
                             />
                         )}
                     </div>
                 </div>
            </div>

            {/* Value Input Part */}
            <div className="w-2/3 min-h-6 pl-2 flex items-center value-input-container">
                {renderValueInput()}
            </div>
        </div>
    );
}); 